import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { catchError, throwError } from 'rxjs';

import { AuthService } from './auth.service';

/**
 * Fängt abgelaufene und widerrufene Sessions ab.
 *
 * WARUM DAS NÖTIG IST: Bis hierher gab es in der ganzen App KEINE
 * 401-Behandlung. `AuthService.getToken()` prüft nur `exp` — ein per
 * `token_version` widerrufenes Token gilt clientseitig weiter als gültig.
 * Ohne diesen Interceptor bliebe ein zweites Gerät nach einem Passwortwechsel
 * scheinbar angemeldet: `authGuard` lässt durch, jeder API-Aufruf antwortet
 * 401, und die Seiten rendern stumm leere Zustände. Genau der Effekt, den der
 * Passwortwechsel verhindern soll, wäre für den Nutzer unsichtbar.
 *
 * Der Login selbst ist ausgenommen — dort IST ein 401 die normale Antwort auf
 * falsche Zugangsdaten und wird von der Komponente behandelt.
 */
const EXEMPT_PATHS = [
  '/api/auth/login/',
  '/api/auth/register/',
  '/api/auth/password/reset/',
  '/api/auth/email/change/confirm/',
  '/api/auth/email/change/revert/',
  '/api/auth/managed/',
];

export const authErrorInterceptorFn: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const snackBar = inject(MatSnackBar);
  const translate = inject(TranslateService);

  return next(req).pipe(
    catchError((error: unknown) => {
      const is401 = error instanceof HttpErrorResponse && error.status === 401;
      const isApi = req.url.includes('/api/');
      const isExempt = EXEMPT_PATHS.some((p) => req.url.includes(p));

      if (is401 && isApi && !isExempt && auth.isAuthenticated()) {
        const revoked = (error as HttpErrorResponse).error?.code === 'token_revoked';
        auth.logout();
        snackBar.open(
          translate.instant(revoked ? 'account.session_revoked' : 'account.session_expired'),
          '',
          { duration: 6000 },
        );
        router.navigate(['/login'], {
          queryParams: { returnUrl: router.url },
        });
      }

      return throwError(() => error);
    }),
  );
};
